import {
    ChangeDetectorRef,
    Component,
    DoCheck,
    forwardRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomElement } from '../../../classes/custom-element.class';
import { createNumberMask } from 'text-mask-addons/dist/textMaskAddons';
import numeral from 'numeral-es6';
import 'numeral/locales/nl-nl';
import 'numeral/locales/en-gb';
import { CurrencyPipe } from '@kaiser-software/chassis-core/src/app/currency/pipes/currency.pipe';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => NumberInputComponent),
    multi: true
};

@Component({
    selector: 'chassis-number-input',
    templateUrl: './number.component.html',
    styleUrls: ['./number.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, CurrencyPipe]
})
export class NumberInputComponent extends CustomElement implements OnInit, DoCheck, OnChanges {

    @Input() type = 'number';

    @Input() placeholder = '';

    @Input() min: number;

    @Input() max: number;

    @Input() decimals: number;

    @Input() disabled: boolean;

    @Input() required: boolean;

    @Input() separators = true;

    @Input() currency = null;

    /**
     * The value that holds a non formatted value.
     */
    public inner: number | string;

    private locale: string;

    public mask;

    /**
     * Constructor
     *
     * @param cdr
     * @param currencyPipe
     */
    constructor(protected cdr: ChangeDetectorRef, protected currencyPipe: CurrencyPipe) {
        super();
    }

    /**
     * Init
     *
     * @return void
     */
    public ngOnInit(): void {
        this.setLocale();
        this.mutateAttributeValues();
        this.setMask();
    }

    /**
     * Set the locale
     *
     * @return void
     */
    protected setLocale(): void {
        const defaultCurrency = this.currencyPipe.getCurrency(this.currency);

        let locale;
        switch (defaultCurrency.code.toUpperCase()) {
            case 'USD':
                locale = 'en-gb';
                break;
            case 'EUR':
                locale = 'nl-nl';
                break;
        }

        numeral.locale(locale);

        this.locale = locale;
    }

    /**
     * Mutates the attribute values
     *
     * @return void
     */
    protected mutateAttributeValues(): void {
        // We'll set the attributes value to boolean values
        // even when no value was given. This is because these
        // attributes are boolean attributes and do not need
        // a value
        this.disabled = this.disabled !== undefined && this.disabled !== false;
        this.required = this.required !== undefined && this.required !== false;

        this.mutateDecimalsAttribute();
    }

    /**
     * Mutates the decimals
     *
     * @return void
     */
    protected mutateDecimalsAttribute(): void {
        this.decimals = this.decimals === undefined ? (this.isType('currency')
            ? 2 : 0) : this.decimals;
    }

    /**
     * Set the options fo the mask
     *
     * @param options
     * @return void
     */
    public setMask(options = {}): void {
        // Get the locale values from numeral.js
        const locale = numeral.locales[this.locale];

        if (this.isType('currency')) {
            const currencySettings = this.currencyPipe.getCurrency(this.currency);

            this.mask = createNumberMask(Object.assign({
                prefix: currencySettings.symbol + ' ',
                suffix: '',
                allowDecimals: currencySettings['settings'].decimals > 0,
                requireDecimal: currencySettings['settings'].decimals > 0,
                thousandsSeparatorSymbol: currencySettings['settings'].thousand,
                decimalSymbol: currencySettings['settings'].decimal,
                decimalLimit: currencySettings['settings'].decimals,
            }, options));
        } else {
            this.mask = createNumberMask(Object.assign({
                prefix: '',
                suffix: '',
                allowDecimals: this.decimals > 0,
                requireDecimal: this.decimals > 0,
                thousandsSeparatorSymbol: this.separators ? locale.delimiters.thousands : '',
                decimalSymbol: locale.delimiters.decimal,
                decimalLimit: this.decimals,
                allowNegative: true
            }, options));
        }
    }

    /**
     * The value property setter.
     *
     * @param {string | Array<string>} value
     * @return void
     */
    public set inputValue(value: number | string) {
        if (value) {
            if (Number(this.max) && Number(value) >= Number(this.max)) {
                value = Number(this.max);

                // Limit the number of characters to prevent more characters than
                // the maximum value length
                this.setMask({
                    integerLimit: (this.max && this.inner <= this.max)
                        ? this.max.toString().length : false,
                });
            }
            if (Number(this.min) && Number(value) <= Number(this.min)) {
                value = Number(this.min);

                this.setMask();
            }
        }

        this.inner = value;

        // Pass the formatted number value to
        // the input component
        this.val = this.formatNumberValue(value);
    }

    /**
     * Formats the given value as number
     *
     * @param value
     * @return number
     */
    protected formatNumberValue(value: string | number): number {
        numeral.locale(this.locale);

        return Number(numeral(value.toString()).value());
    }

    /**
     * Checks if the value is initialized
     *
     * @return boolean
     */
    protected valueNotInitialized(): boolean {
        return !this.inner && this.val;
    }

    /**
     * The value property getter.
     *
     * @return number | string
     */
    public get inputValue(): number | string {
        if (this.valueNotInitialized()) {
            this.inner = this.val = Number(this.val);

            this.padDecimals();
        }

        return this.inner;
    }

    /**
     * Check that is executed every cycle
     *
     * @return void
     */
    ngDoCheck(): void {
        if (this.modelValueChanged()) {
            this.inner = Number(this.val);

            this.cdr.detectChanges();
        }
    }

    /**
     * Change detection
     *
     * @param changes
     * @return void
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currency) {
            this.setLocale();
            this.setMask();
            this.padDecimals();
        }
    }

    /**
     * Checks if the value is changed
     *
     * @return boolean
     */
    protected modelValueChanged(): boolean {
        return this.inner !== undefined &&
            this.val !== this.formatNumberValue(this.inner)
    }

    /**
     * Add trailing zero's to the value on blur of the input.
     *
     * @return void
     */
    public padDecimals(): void {
        if (this.val !== undefined && this.decimals) {
            const chunks = (this.val.toString() || '')
                .split('.');

            const int    = chunks[0];
            let decimals = chunks[1];

            decimals = decimals ? decimals.padEnd(this.decimals, '0') : '00';

            // Get the locale values from numeral.js
            numeral.locale(this.locale);
            const locale = numeral.locales[this.locale];

            const delimiter = locale.delimiters.decimal;

            this.inputValue = int + delimiter + decimals;
        }
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/elements/components/number/number/number.component.ts